import React from 'react';
import { Scale, ShieldCheck, HardDrive, ArrowRight, User, Briefcase, FileText, Settings } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
interface LoginProps {
  onLogin: (personaId: string) => void;
}
export function Login({
  onLogin
}: LoginProps) {
  const personas = [{
    id: 'EF',
    name: 'Edward Festeryga',
    role: 'Managing Attorney / Trial Lead',
    initials: 'EF',
    icon: Briefcase,
    description: 'Executive dashboard with case risks, approvals, and critical evidence summaries.',
    defaultPage: 'Dashboard',
    highlight: true
  }, {
    id: 'AT',
    name: 'Ana Torres',
    role: 'Paralegal Reviewer',
    initials: 'AT',
    icon: FileText,
    description: 'Chronology review, document validation, provider timelines, and extraction quality checks.',
    defaultPage: 'Chronology',
    highlight: false
  }, {
    id: 'MS',
    name: 'Maria Santos',
    role: 'Intake Coordinator',
    initials: 'MS',
    icon: User,
    description: 'New matter setup, intake workflows, email filing, and folder organization.',
    defaultPage: 'Matters',
    highlight: false
  }, {
    id: 'RK',
    name: 'Robert Kim',
    role: 'Litigation Support',
    initials: 'RK',
    icon: Settings,
    description: 'Bates workflows, production classification, OCR monitoring, and audit queue.',
    defaultPage: 'Workflows',
    highlight: false
  }];
  return <div className="min-h-screen w-full flex flex-col items-center justify-center bg-slate-900 p-4 md:p-8" data-id="element-1334">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1335">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-red-900/20 blur-[120px]" data-id="element-1336" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[40%] rounded-full bg-slate-800/40 blur-[100px]" data-id="element-1337" />
      </div>

      <div className="w-full max-w-5xl relative z-10 flex flex-col items-center" data-id="element-1338">
        <div className="flex flex-col items-center mb-10 text-center" data-id="element-1339">
          <div className="bg-accent p-3 rounded-xl mb-4 shadow-lg shadow-red-900/50" data-id="element-1340">
            <Scale className="w-8 h-8 text-white" data-id="element-1341" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-heading" data-id="element-1342">
            Festeryga Law
          </h1>
          <p className="text-slate-400 text-base md:text-lg mt-2" data-id="element-1343">
            Local Legal Intelligence Platform
          </p>
          <p className="text-accent italic text-sm mt-2 font-medium" data-id="element-1344">
            When It's Everything™
          </p>
        </div>

        <div className="w-full mb-8 text-center" data-id="element-1345">
          <h2 className="text-xl font-semibold text-white mb-2" data-id="element-1346">
            Select Demo Persona
          </h2>
          <p className="text-slate-400 text-sm max-w-2xl mx-auto" data-id="element-1347">
            Experience the platform through different roles. Each persona has a
            tailored dashboard, specific permissions, and a unique default
            landing page.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl" data-id="element-1348">
          {personas.map(persona => <Card key={persona.id} className={`border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-xl hover:shadow-2xl transition-all duration-300 group cursor-pointer overflow-hidden relative ${persona.highlight ? 'ring-2 ring-accent/50 md:scale-[1.02]' : 'hover:border-slate-600'}`} onClick={() => onLogin(persona.id)} data-id="element-1349">

              {persona.highlight && <div className="absolute top-0 right-0 bg-accent text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider z-10" data-id="element-1350">
                  Primary Demo
                </div>}
              <CardContent className="p-6 flex flex-col h-full" data-id="element-1351">
                <div className="flex items-start space-x-4 mb-4" data-id="element-1352">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shrink-0 ${persona.highlight ? 'bg-accent text-white shadow-lg shadow-red-900/50' : 'bg-slate-800 text-slate-300 group-hover:bg-slate-700 transition-colors'}`} data-id="element-1353">

                    {persona.initials}
                  </div>
                  <div data-id="element-1354">
                    <h3 className="text-lg font-bold text-white group-hover:text-accent transition-colors" data-id="element-1355">
                      {persona.name}
                    </h3>
                    <p className="text-sm text-slate-400 font-medium flex items-center mt-0.5" data-id="element-1356">
                      <persona.icon className="w-3.5 h-3.5 mr-1.5" data-id="element-1357" />
                      {persona.role}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-1" data-id="element-1358">
                  {persona.description}
                </p>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-800/50" data-id="element-1359">
                  <div className="text-xs text-slate-500" data-id="element-1360">
                    Starts at:{' '}
                    <span className="text-slate-300 font-medium" data-id="element-1361">
                      {persona.defaultPage}
                    </span>
                  </div>
                  <Button variant={persona.highlight ? 'primary' : 'secondary'} className={`h-8 text-xs ${persona.highlight ? 'bg-accent hover:bg-accent/90 text-white' : 'bg-slate-800 text-white hover:bg-slate-700'}`} onClick={e => {
                e.stopPropagation();
                onLogin(persona.id);
              }} data-id="element-1362">

                    Enter as {persona.initials}{' '}
                    <ArrowRight className="w-3 h-3 ml-1.5" data-id="element-1363" />
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-xs text-slate-500" data-id="element-1364">
          <div className="flex items-center" data-id="element-1365">
            <ShieldCheck className="w-4 h-4 mr-2 text-emerald-500/70" data-id="element-1366" />
            End-to-end encrypted connection
          </div>
          <div className="hidden sm:block w-1 h-1 rounded-full bg-slate-700" data-id="element-1367"></div>
          <div className="flex items-center" data-id="element-1368">
            <HardDrive className="w-4 h-4 mr-2 text-accent/70" data-id="element-1369" />
            Data remains on local firm servers
          </div>
        </div>
      </div>
    </div>;
}